import { round } from 'mathjs/number';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Format a date to YYYY-MM-DD
 *
 * @param {Date | string | null} date - Date
 * @returns {string} Formatted date string
 */
export const formatToDate = (date: Date | string | null): string => {
    if (!date) return 'N/A';

    return new Date(date).toISOString().split('T')[0];
};

/**
 * Formats a date into "yyyy-MM-dd HH:mm:ss" using the user's configured timezone.
 *
 * @param date - The date to format (Date object, ISO string, or null)
 * @param timezone - Timezone string (e.g., "Europe/Helsinki"). Defaults to "UTC"
 * @returns Formatted date string or "N/A" if date is not provided
 */
export const formatDateTimeInTimeZone = (date: Date | string | null, timezone: string = 'UTC'): string => {
    if (!date) return 'N/A';

    return formatInTimeZone(date, timezone, 'yyyy-MM-dd HH:mm:ss');
};

/**
 * Format a status string to capitalize the first letter
 *
 * @param {string | null} status - Status string
 * @returns {string} Formatted status string
 */
export const formatToTitleCase = (status: string | null): string => {
    if (!status) return 'N/A';

    return status.toString().charAt(0).toUpperCase() + status.toString().slice(1);
};

/**
 * Formats a number of seconds as a duration.
 * Under an hour it returns MM:SS, an hour or more returns HH:MM:SS.
 *
 * @param {number | null} duration - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatSecondsToTime = (duration: number | null): string => {
    if (typeof duration !== 'number' || duration === null) {
        return 'N/A';
    }

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);

    const pad = (n: number) => n.toString().padStart(2, '0');

    // Use MM:SS format if under 1 hour, otherwise HH:MM:SS
    if (hours === 0) {
        return `${pad(minutes)}:${pad(seconds)}`;
    }

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Format text to a pluralized format based on a given amount.
 *
 * @param {Object} params
 * @param {number} params.count - Amount
 * @param {string} params.singular - String in singular form
 * @param {string} params.plural - String in plural form
 * @param {boolean} [params.showCount=true] - Whether to show the count
 * @returns {string}
 */
export const formatToPlural = ({
    count,
    singular,
    plural,
    showCount = true,
}: {
    count: number | null;
    singular: string | null;
    plural: string | null;
    showCount?: boolean;
}): string => {
    if (count == null || !singular || !plural) {
        return '';
    }

    const formattedString = count === 1 ? singular : plural;

    return showCount ? `${count} ${formattedString}` : formattedString;
};

/**
 * Format a decimal to a percentage.
 *
 * @param {number} decimal - Decimal
 * @returns {string} Formatted percentage string
 */
export const formatDecimalToPercentage = (decimal: number): string => {
    return `${Math.round(decimal * 100)}%`;
};

/**
 * Rounds a number to two decimal places using mathjs.
 *
 * @param {number} n - Number to round
 * @returns {number} - Rounded number
 */
export const roundToTwo = (n: number): number => {
    if (!isFinite(n)) {
        return NaN;
    }

    return round(n, 2);
};

/**
 * Formats a timestamp the way the banner shows it:
 * "Today 10:42", "Yesterday 09:15", or "Jul 20 14:30" for anything older.
 * Returns an empty string if the date cannot be parsed.
 */
export const formatRelativeDateTime = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return `Today ${time}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday ${time}`;
    }
    return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
};
